import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

/**
 * Semantic status badges. Each tone pairs a colour with an icon and an
 * `aria-label`, so meaning is conveyed even to users who cannot perceive
 * colour or who rely on screen readers (WCAG 1.4.1 — Use of Color).
 */
export type StatusBadgeTone = "success" | "warning" | "danger" | "info" | "neutral"

const statusBadgeTone: Record<StatusBadgeTone, { className: string; Icon: LucideIcon; srLabel: string }> = {
  success: {
    className: "border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 text-green-800 dark:text-green-300",
    Icon: CheckCircle2,
    srLabel: "正常",
  },
  warning: {
    className: "border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300",
    Icon: AlertTriangle,
    srLabel: "注意",
  },
  danger: {
    className: "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300",
    Icon: AlertCircle,
    srLabel: "エラー",
  },
  info: {
    className: "border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300",
    Icon: Info,
    srLabel: "情報",
  },
  neutral: {
    className: "border-border bg-muted text-foreground",
    Icon: Info,
    srLabel: "ステータス",
  },
}

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: StatusBadgeTone
  /**
   * Optional override for the icon. Pass `null` to omit the icon entirely
   * (NOT recommended — colour alone should never carry meaning).
   */
  icon?: React.ReactNode
  /**
   * Screen-reader-only prefix announcing the tone, e.g. "状態: 正常".
   * Defaults to a Japanese tone label. Set to `false` to disable.
   */
  srPrefix?: string | false
  children: React.ReactNode
}

function StatusBadge({
  tone,
  icon,
  srPrefix,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const meta = statusBadgeTone[tone]
  const Icon = meta.Icon
  const renderedIcon =
    icon === undefined ? <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden /> : icon
  const prefix = srPrefix === false ? null : srPrefix ?? `状態: ${meta.srLabel} — `

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        meta.className,
        className,
      )}
      {...props}
    >
      {prefix && <span className="sr-only">{prefix}</span>}
      {renderedIcon}
      <span>{children}</span>
    </span>
  )
}

export { Badge, badgeVariants, StatusBadge }
